
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Styleclasses from "./UploadDoc.module.css";
import { FileUploader } from "react-drag-drop-files";
import { upload_with_token, save_documents_with_token } from "Services/Requests";
import Upload from "Assets/upload.png";
import Load from "Components/Loader/loader";
import { showSuccessNotification } from "Utils/Notifications";
import { showWarningAlert } from "Utils/Alerts";
import { FaDownload, FaCheckCircle } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

type PendingFile = {
    id: string; // url or unique id
    file: File;
    file_name: string;
    file_type: string;
    comments: string;
    upload_status: 'pending' | 'uploading' | 'success' | 'error';
    download_id?: string;
    file_url?: string;
}

const Fileupload = () => {
    return (
        <div className={Styleclasses["File-upload"]}>
            <img src={Upload} alt="upload" />
            <p>Upload File</p>
        </div>
    );
};

const DocTypes = [
    "W2-wage statement",
    "Personal id proof",
    "1099 INT",
    "1099 DIV",
    "1099-G State Tax Refund",
    "1099 MISC ( Self-employed business income )",
    "1099-B Stock Doc",
    "1099 -R",
    "1099-R (401k plan)",
    "5498 -SA ",
    "1098-T (Tuition fees)",
    "1098-E (interest on your student loan)",
    "1120-K1",
    "1065-k1",
    "1042-S",
    "Residents of MA - 1099HC",
    "Child/Dependent Care expenses (SSN/Tax ID required)",
    "Other",
];

function UploadDocumentPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [loading, setLoading] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        if (!token) {
            showWarningAlert("Invalid access token. Please use a valid link.");
        }
    }, [token]);

    const uploadSingleFile = async (fileItem: PendingFile) => {
        if (!token) return;

        setPendingFiles((prev) => prev.map((f) => f.id === fileItem.id ? { ...f, upload_status: 'uploading' } : f));

        const formD = new FormData();
        formD.append("file", fileItem.file);
        formD.append("folder", "documents");

        try {
            const uploadRes: any = await upload_with_token(formD, token);
            if (uploadRes?.data?.status === 1) {
                setPendingFiles((prev) => prev.map((f) => f.id === fileItem.id ? {
                    ...f,
                    upload_status: 'success',
                    download_id: uploadRes.data.fileName,
                    file_url: uploadRes.data.fileUrl
                } : f));
            } else {
                setPendingFiles((prev) => prev.map((f) => f.id === fileItem.id ? { ...f, upload_status: 'error' } : f));
                showWarningAlert(`Failed to upload ${fileItem.file.name}`);
            }
        } catch (err: any) {
            console.error("Error uploading file", fileItem.file.name, err);
            setPendingFiles((prev) => prev.map((f) => f.id === fileItem.id ? { ...f, upload_status: 'error' } : f));
            showWarningAlert(`Error uploading ${fileItem.file.name}`);
        }
    };

    const handleFilesChange = (files: File[]) => {
        if (!token) {
            showWarningAlert("Token is missing. Unable to upload file.");
            return;
        }

        const newFiles: PendingFile[] = Array.from(files).map((file) => ({
            id: Math.random().toString(36).substring(7),
            file: file,
            file_name: "",
            file_type: "",
            comments: "",
            upload_status: 'pending'
        }));

        setPendingFiles((prev) => [...prev, ...newFiles]);

        // Trigger upload for each new file
        newFiles.forEach(file => uploadSingleFile(file));
    };

    const handleDelete = (id: string) => {
        setPendingFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleUpdateFile = (id: string, field: keyof PendingFile, value: string) => {
        setPendingFiles((prev) => prev.map((f) => f.id === id ? { ...f, [field]: value } : f));
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            showWarningAlert("Token is missing. Unable to save document.");
            return;
        }

        if (pendingFiles.length === 0) {
            showWarningAlert("Please select at least one file to upload.");
            return;
        }

        // Validate that all files are uploaded and have metadata
        const readyFiles = [];
        for (const file of pendingFiles) {
            if (file.upload_status === 'uploading') {
                showWarningAlert("Please wait for all files to finish uploading.");
                return;
            }
            if (file.upload_status === 'error') {
                showWarningAlert(`Please remove failed uploads before submitting: ${file.file.name}`);
                return;
            }
            if (!file.file_name.trim()) {
                showWarningAlert(`Document Name is required for file: ${file.file.name}`);
                return;
            }
            if (!file.file_type) {
                showWarningAlert(`Document Type is required for file: ${file.file.name}`);
                return;
            }
            if (file.upload_status === 'success' && file.download_id) {
                readyFiles.push({
                    file_name: file.file_name,
                    file_type: file.file_type,
                    comments: file.comments,
                    download_id: file.download_id
                });
            }
        }

        setLoading(true);

        try {
            if (readyFiles.length > 0) {
                const saveRes = await save_documents_with_token(readyFiles, token);
                if (saveRes.data.Status === 1) {
                    showSuccessNotification("All documents saved successfully!");
                    setIsSubmitted(true);
                } else {
                    showWarningAlert(saveRes?.data.Message || "Metadata save failed.");
                }
            } else {
                showWarningAlert("No files ready to submit.");
            }

        } catch (err: any) {
            showWarningAlert(err.message || "Something went wrong during save.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={Styleclasses["Container"]}>
            {loading && <Load />}
            {token ? (
                <form onSubmit={handleSave}>
                    <div className={Styleclasses["info"]}>
                        <h3>Upload Documents</h3>

                        {!isSubmitted && (
                            <div
                                className={Styleclasses["form-control"]}
                                style={{ gridColumn: "1/-1" }}>
                                <label>Upload Files</label>
                                <FileUploader
                                    handleChange={handleFilesChange}
                                    name="file"
                                    multiple={true}
                                    children={<Fileupload />}
                                />
                            </div>
                        )}

                        {pendingFiles.length > 0 && (
                            <div style={{ gridColumn: "1/-1", display: "grid", gap: "1rem", marginTop: "1rem" }}>
                                <h4>{isSubmitted ? "Uploaded Documents" : `Selected Files (${pendingFiles.length})`}</h4>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                                        <thead>
                                            <tr style={{ borderBottom: "1px solid #ccc", textAlign: "left" }}>
                                                <th style={{ padding: "1rem" }}>File Name</th>
                                                <th style={{ padding: "1rem" }}>Document Name <span style={{ color: "red" }}>*</span></th>
                                                <th style={{ padding: "1rem" }}>Document Type <span style={{ color: "red" }}>*</span></th>
                                                <th style={{ padding: "1rem" }}>Comments</th>
                                                <th style={{ padding: "1rem" }}>{isSubmitted ? "Status" : "Action"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingFiles.map((item, index) => (
                                                <tr key={item.id} style={{ borderBottom: "1px solid #eee" }}>
                                                    <td style={{ padding: "1rem", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.file.name}>
                                                        {item.upload_status === 'uploading' && <span style={{ color: "orange" }}>Uploading...</span>}
                                                        {item.upload_status === 'error' && <span style={{ color: "red" }}>Failed</span>}
                                                        {item.upload_status === 'pending' && <span>Pending...</span>}
                                                        {item.upload_status === 'success' && item.file_url && (
                                                            <a href={item.file_url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#2db2bc", textDecoration: "none" }}>
                                                                <FaDownload /> Download
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "1rem" }}>
                                                        <input
                                                            type="text"
                                                            className={Styleclasses["form-control"]} // reusing generic input style if applicable, or inline
                                                            style={{ width: "100%", padding: "0.5rem", borderRadius: "5px", border: "1px solid #ccc" }}
                                                            value={item.file_name}
                                                            onChange={(e) => handleUpdateFile(item.id, "file_name", e.target.value)}
                                                            placeholder="Doc Name"
                                                            disabled={isSubmitted}
                                                        />
                                                    </td>
                                                    <td style={{ padding: "1rem" }}>
                                                        <select
                                                            style={{ width: "100%", padding: "0.5rem", borderRadius: "5px", border: "1px solid #ccc" }}
                                                            value={item.file_type}
                                                            onChange={(e) => handleUpdateFile(item.id, "file_type", e.target.value)}
                                                            disabled={isSubmitted}
                                                        >
                                                            <option value="">Select Type</option>
                                                            {DocTypes.map((type, idx) => (
                                                                <option key={idx} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: "1rem" }}>
                                                        <input
                                                            type="text"
                                                            style={{ width: "100%", padding: "0.5rem", borderRadius: "5px", border: "1px solid #ccc" }}
                                                            value={item.comments}
                                                            onChange={(e) => handleUpdateFile(item.id, "comments", e.target.value)}
                                                            placeholder="Comments"
                                                            disabled={isSubmitted}
                                                        />
                                                    </td>
                                                    <td style={{ padding: "1rem", textAlign: "center" }}>
                                                        {isSubmitted ? (
                                                            <FaCheckCircle color="green" size={20} />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDelete(item.id)}
                                                                style={{ background: "none", border: "none", cursor: "pointer" }}
                                                            >
                                                                <MdDelete color="red" size={20} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div
                            className={Styleclasses["action-btns"]}
                            style={{ gridColumn: "1/-1", marginTop: "1rem" }}>
                            <button
                                style={{ width: "9rem" }}
                                disabled={pendingFiles.length === 0 || isSubmitted}
                            >
                                {isSubmitted ? "Saved" : "Submit All"}
                            </button>
                        </div>
                    </div>
                </form>
            ) : (
                <div className={Styleclasses["info"]}>
                    <h3>Invalid Access</h3>
                    <p>Please use a valid link with authentication token.</p>
                </div>
            )}
        </div>
    );
}

export default UploadDocumentPage;
